import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { LocationDetails } from '@rma/public/locations/public/api-location-details';
import { Observable, switchMap, tap } from 'rxjs';
import { ShortlistResult } from '../domain/shortlist';
import { ApiShortlistStorageService } from './api-shortlist-storage.service';
import { LocalShortlistStorageService } from './local-shortlist-storage.service';

export interface CompareAgent {
  shortlistCount: Signal<number>;
  shortlist: Signal<string[]>;
  // The shortlist as last loaded from storage — moved only by a load or an auth flip, never by a
  // local add/remove. A consumer that already applies a mutation to its own state optimistically
  // can track this instead of `shortlist` so that mutation doesn't echo back as a redundant fetch.
  // Only safe for the mutations the consumer does patch itself: track `shortlist` for the rest, or
  // it will miss them. Read `shortlist` for the live codes — this one lags by any local mutation.
  loadedShortlist: Signal<string[]>;
  location: LocationDetails | undefined;
  remove(agentCode: string): Observable<ShortlistResult>;
}

@Injectable({
  providedIn: 'root',
})
export class ShortlistService implements CompareAgent {
  private readonly userService = inject(PublicUserContextService);
  private readonly localShortlistStorage = inject(LocalShortlistStorageService);
  private readonly apiShortlistStorage = inject(ApiShortlistStorageService);

  private readonly agentCodes = signal<string[]>([]);
  private readonly loadedCodes = signal<string[]>([]);
  private readonly authenticated = signal(false);
  private readonly storage = computed(() => (this.authenticated() ? this.apiShortlistStorage : this.localShortlistStorage));

  public readonly shortlist = this.agentCodes.asReadonly();
  public readonly loadedShortlist = this.loadedCodes.asReadonly();
  public readonly shortlistCount = computed(() => this.shortlist().length);
  public readonly location = undefined;

  public constructor() {
    // Reload whenever auth flips storage backends. switchMap cancels an in-flight
    // load so a slow API response can't clobber a newer local one on rapid toggles.
    this.userService.isAuthenticated$
      .pipe(
        tap((isAuthenticated) => this.authenticated.set(isAuthenticated)),
        switchMap(() => this.storage().load()),
        takeUntilDestroyed(),
      )
      .subscribe((agentCodes) => {
        this.agentCodes.set(agentCodes);
        this.loadedCodes.set(agentCodes);
      });
  }

  // Deliberately not on `CompareAgent`: that contract is also implemented by LocationCompareService,
  // which holds no storage and has nothing to clear.
  public clear(): Observable<ShortlistResult[]> {
    return this.storage()
      .clear()
      .pipe(
        tap((results) => {
          // Codes the store failed to drop are still shortlisted, so they stay. Emptying both
          // signals regardless would hide them from the user until the next load contradicted us.
          const remaining = results.filter(({ success }) => !success).map(({ agentCode }) => agentCode);

          this.agentCodes.set(remaining);
          this.loadedCodes.set(remaining);
        }),
      );
  }

  public remove(agentCode: string): Observable<ShortlistResult> {
    return this.storage()
      .remove(agentCode)
      .pipe(
        tap((result) => {
          if (result) {
            this.agentCodes.update((codes) => codes.filter((code) => code !== agentCode));
          }
        }),
      );
  }

  public toggleShortList(agentCode: string): Observable<ShortlistResult> {
    return this.agentCodes().includes(agentCode) ? this.remove(agentCode) : this.add(agentCode);
  }

  public addMany(agentCodes: string[]): Observable<ShortlistResult[]> {
    return this.storage()
      .addMany(agentCodes)
      .pipe(
        tap((results) => {
          const succeeded = results.filter(({ success }) => success).map(({ agentCode }) => agentCode);
          this.append(succeeded);
        }),
      );
  }

  private add(agentCode: string): Observable<ShortlistResult> {
    return this.storage()
      .add(agentCode)
      .pipe(
        tap((result) => {
          if (result.success) {
            this.append([agentCode]);
          }
        }),
      );
  }

  private append(agentCodes: string[]): void {
    this.agentCodes.update((codes) => {
      const additions = agentCodes.filter((code) => !codes.includes(code));
      return additions.length ? [...codes, ...additions] : codes;
    });
  }
}
